import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Divider,
  Chip,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import { authGet, authPost, authPut, authDelete } from "../../lib";

const useStyles = makeStyles((theme) => ({
  root: { maxWidth: 1100, width: "100%", margin: "0 auto", padding: theme.spacing(3) },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  filterInput: { width: 280 },
  typeDataCell: { width: "28%" },
  stubCell: { width: "32%" },
  seoCell: { width: 80 },
  editCell: { width: 52, padding: "0 4px" },
  deleteCell: { width: 52, padding: "0 4px" },
  emptyCell: { color: "#999", padding: theme.spacing(3), textAlign: "center" },
  dialogSection: { marginBottom: theme.spacing(2) },
  sectionLabel: {
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  fieldRow: { display: "flex", gap: theme.spacing(2), marginBottom: theme.spacing(1.5) },
  seoBadge: { height: 20, fontSize: "0.7rem" },
}));

const EMPTY_FORM = { stub: "", type_data: "", title: "", description: "", keywords: "", h1: "", h2: "" };

function hasSeo(row) {
  return !!(row.title || row.h1 || row.description || row.keywords || row.h2);
}

export function UrlStubsPage() {
  const classes = useStyles();

  const [stubs, setStubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // null = adding
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const [snack, setSnack] = useState({ open: false, msg: "" });

  useEffect(() => { fetchStubs(); }, []);

  async function fetchStubs() {
    setLoading(true);
    const { data, error } = await authGet("/url-stubs");
    setLoading(false);
    if (error) { showSnack(error?.message || "Failed to load stubs"); return; }
    setStubs(data || []);
  }

  function showSnack(msg) { setSnack({ open: true, msg }); }

  function openAdd() {
    setEditTarget(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  }

  function openEdit(row) {
    setEditTarget(row);
    setForm({
      stub: row.stub || "",
      type_data: row.type_data || "",
      title: row.title || "",
      description: row.description || "",
      keywords: row.keywords || "",
      h1: row.h1 || "",
      h2: row.h2 || "",
    });
    setDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditTarget(null);
  }

  function setField(key) {
    return (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
  }

  async function handleSave() {
    if (!form.stub.trim() || !form.type_data.trim()) return;
    setSaving(true);
    if (editTarget) {
      const { data, error } = await authPut(`/url-stubs/${editTarget.router_id}`, form);
      setSaving(false);
      if (error) { showSnack(error?.message || "Failed to update stub"); return; }
      setStubs((prev) =>
        prev.map((s) => (s.router_id === editTarget.router_id ? data : s))
      );
      showSnack("Stub updated");
    } else {
      const { data, error } = await authPost("/url-stubs", form);
      setSaving(false);
      if (error) { showSnack(error?.message || "Failed to add stub"); return; }
      setStubs((prev) => [...prev, data].sort((a, b) => (a.type_data || "").localeCompare(b.type_data || "")));
      showSnack("Stub added");
    }
    closeDialog();
  }

  async function handleDelete() {
    const { error } = await authDelete(`/url-stubs/${confirmDeleteId}`);
    if (error) { showSnack(error?.message || "Failed to delete stub"); return; }
    setStubs((prev) => prev.filter((s) => s.router_id !== confirmDeleteId));
    setConfirmDeleteId(null);
    showSnack("Stub deleted");
  }

  const filterLower = filter.toLowerCase();
  const visible = filter
    ? stubs.filter(
        (s) =>
          (s.stub || "").toLowerCase().includes(filterLower) ||
          (s.type_data || "").toLowerCase().includes(filterLower)
      )
    : stubs;

  const deleteTarget = stubs.find((s) => s.router_id === confirmDeleteId);
  const canSave = form.stub.trim() && form.type_data.trim();

  return (
    <Box className={classes.root}>
      <Typography variant="h6" gutterBottom>URL Stubs</Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
        Collection and landing page URL slugs (<code>type=items</code>), with optional SEO metadata.
      </Typography>

      <Box className={classes.toolbar}>
        <TextField
          size="small"
          variant="outlined"
          placeholder="Filter by stub or type data…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className={classes.filterInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" style={{ color: "#aaa" }} />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" color="textSecondary" style={{ flex: 1 }}>
          {visible.length} of {stubs.length} stub{stubs.length !== 1 ? "s" : ""}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="small"
          startIcon={<AddIcon />}
          onClick={openAdd}
        >
          Add Stub
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell className={classes.typeDataCell}>Type Data</TableCell>
              <TableCell className={classes.stubCell}>Stub (URL slug)</TableCell>
              <TableCell className={classes.seoCell} align="center">SEO</TableCell>
              <TableCell className={classes.editCell} />
              <TableCell className={classes.deleteCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center" style={{ padding: 24 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : visible.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className={classes.emptyCell}>
                  {filter ? "No matching stubs" : "No stubs yet"}
                </TableCell>
              </TableRow>
            ) : (
              visible.map((s) => (
                <TableRow key={s.router_id} hover>
                  <TableCell>
                    <Typography variant="body2" style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                      {s.type_data}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" style={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
                      {s.stub}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {hasSeo(s) && (
                      <Chip label="SEO" size="small" color="primary" className={classes.seoBadge} />
                    )}
                  </TableCell>
                  <TableCell className={classes.editCell} align="center">
                    <IconButton size="small" onClick={() => openEdit(s)} title="Edit">
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                  <TableCell className={classes.deleteCell} align="center">
                    <IconButton size="small" onClick={() => setConfirmDeleteId(s.router_id)} title="Delete">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editTarget ? "Edit Stub" : "Add Stub"}</DialogTitle>
        <DialogContent>
          <Box className={classes.dialogSection}>
            <Typography className={classes.sectionLabel}>Stub</Typography>
            <Box className={classes.fieldRow}>
              <TextField
                label="Stub (URL slug)"
                size="small"
                variant="outlined"
                fullWidth
                value={form.stub}
                onChange={setField("stub")}
                helperText="Spaces and special chars will be converted to hyphens"
                autoFocus
              />
            </Box>
            <Box className={classes.fieldRow}>
              <TextField
                label="Type Data"
                size="small"
                variant="outlined"
                fullWidth
                value={form.type_data}
                onChange={setField("type_data")}
                helperText='e.g. "brand=12" or "series=45"'
              />
            </Box>
          </Box>

          <Divider style={{ margin: "8px 0 16px" }} />

          <Box className={classes.dialogSection}>
            <Typography className={classes.sectionLabel}>SEO Metadata</Typography>
            <Box className={classes.fieldRow}>
              <TextField
                label="Title"
                size="small"
                variant="outlined"
                fullWidth
                value={form.title}
                onChange={setField("title")}
              />
            </Box>
            <Box className={classes.fieldRow}>
              <TextField
                label="Description"
                size="small"
                variant="outlined"
                fullWidth
                value={form.description}
                onChange={setField("description")}
              />
            </Box>
            <Box className={classes.fieldRow}>
              <TextField
                label="Keywords"
                size="small"
                variant="outlined"
                fullWidth
                value={form.keywords}
                onChange={setField("keywords")}
              />
            </Box>
            <Box className={classes.fieldRow}>
              <TextField
                label="H1"
                size="small"
                variant="outlined"
                style={{ flex: 1 }}
                value={form.h1}
                onChange={setField("h1")}
              />
              <TextField
                label="H2"
                size="small"
                variant="outlined"
                style={{ flex: 1 }}
                value={form.h2}
                onChange={setField("h2")}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={saving || !canSave}
          >
            {saving ? "Saving…" : editTarget ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)}>
        <DialogTitle>Delete Stub</DialogTitle>
        <DialogContent>
          <Typography>
            Delete stub <strong>{deleteTarget?.stub}</strong> (<code>{deleteTarget?.type_data}</code>)?
            This will also remove its SEO metadata and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
          <Button onClick={handleDelete} color="secondary">Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3500}
        onClose={() => setSnack({ ...snack, open: false })}
        message={snack.msg}
      />
    </Box>
  );
}